import { useEffect } from "react";
import type { InvestmentFormData } from "@/domain/validation/investmentSchemas";
import type { BeneficiaryDetail } from "@/domain/beneficiary";
import type { InvestorSummary } from "@/domain/investor";
import type { InvestmentState } from "@/domain/investment";

interface UseInvestmentFormPreselectionParams {
  isOpen: boolean;
  isEditMode: boolean;
  preselectedProject: BeneficiaryDetail | null;
  preselectedInvestor: InvestorSummary | null;
  preselectedState: InvestmentState;
  formData: InvestmentFormData;
  updateFormData: (updates: Partial<InvestmentFormData>) => void;
  setSelectedInvestor: (investor: InvestorSummary | null) => void;
  loadBeneficiaryDetails: (beneficiaryUuid: string, autoSelectQuota: boolean) => void;
  hasPrepopulated: React.MutableRefObject<boolean>;
}

export function useInvestmentFormPreselection({
  isOpen,
  isEditMode,
  preselectedProject,
  preselectedInvestor,
  preselectedState,
  formData,
  updateFormData,
  setSelectedInvestor,
  loadBeneficiaryDetails,
  hasPrepopulated,
}: UseInvestmentFormPreselectionParams) {
  useEffect(() => {
    if (!isOpen) hasPrepopulated.current = false;
  }, [isOpen, hasPrepopulated]);

  useEffect(() => {
    if (isOpen && preselectedState && !isEditMode) updateFormData({ status: preselectedState });
  }, [isOpen, preselectedState, isEditMode, updateFormData]);

  useEffect(() => {
    if (
      isOpen &&
      !isEditMode &&
      preselectedInvestor &&
      formData.investorId !== preselectedInvestor.uuid
    ) {
      updateFormData({ investorId: preselectedInvestor.uuid });
      setSelectedInvestor(preselectedInvestor);
    }
  }, [
    isOpen,
    isEditMode,
    preselectedInvestor,
    formData.investorId,
    updateFormData,
    setSelectedInvestor,
  ]);

  useEffect(() => {
    if (
      isOpen &&
      !isEditMode &&
      preselectedProject &&
      formData.projectId !== preselectedProject.uuid
    ) {
      updateFormData({ projectId: preselectedProject.uuid, quotaId: "" });
      loadBeneficiaryDetails(preselectedProject.uuid, true);
    }
  }, [
    isOpen,
    isEditMode,
    preselectedProject,
    formData.projectId,
    loadBeneficiaryDetails,
    updateFormData,
  ]);
}
